/**
 * Confirmation Dialog Component
 * Reusable confirmation dialog for user confirmations
 */

import * as React from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/modals/alert-dialog'
import { Icon } from '@/components/ui/icon'
import { Input } from '@/components/forms/input'
import { Button } from '@/components/ui/button'

export interface ConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string | React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
  onConfirm: () => void
  onCancel?: () => void
  // Additional props for text confirmation
  confirmText?: string
  onConfirmTextChange?: (text: string) => void
  requiredConfirmText?: string
  confirmButtonText?: string
  confirmButtonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  loading?: boolean
  maxWidth?: string // e.g., 'max-w-6xl', 'max-w-7xl'
  confirmDisabled?: boolean
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  confirmText,
  onConfirmTextChange,
  requiredConfirmText,
  confirmButtonText,
  confirmButtonVariant = 'default',
  loading = false,
  maxWidth = 'max-w-lg',
  confirmDisabled: confirmDisabledProp = false,
}: ConfirmationDialogProps) {
  const handleConfirm = () => {
    // If requiredConfirmText is set, validate it matches confirmText
    if (requiredConfirmText && confirmText !== requiredConfirmText) {
      return
    }
    onConfirm()
    onOpenChange(false)
  }

  const handleCancel = () => {
    onCancel?.()
    onOpenChange(false)
  }

  const isConfirmDisabled =
    confirmDisabledProp || (requiredConfirmText ? confirmText !== requiredConfirmText : false)

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={`${maxWidth} max-h-[90vh] mt-8 mb-8 flex flex-col`}>
        <AlertDialogHeader className="flex-shrink-0 pb-4">
          <AlertDialogTitle>{title}</AlertDialogTitle>
        </AlertDialogHeader>
          {description && (
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            <AlertDialogDescription asChild>
              <div>{description}</div>
            </AlertDialogDescription>
          </div>
          )}
        {requiredConfirmText && onConfirmTextChange && (
          <div className="py-4 flex-shrink-0">
            <Input
              placeholder={`Type "${requiredConfirmText}" to confirm`}
              value={confirmText || ''}
              onChange={(e) => onConfirmTextChange(e.target.value)}
              disabled={loading}
            />
          </div>
        )}
        <AlertDialogFooter className="flex-shrink-0 pt-4 border-t">
          <AlertDialogCancel onClick={handleCancel} disabled={loading} className="flex items-center gap-2">
            <Icon name="X" size={16} />
            {cancelLabel}
          </AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isConfirmDisabled || loading}
            variant={confirmButtonVariant || (variant === 'destructive' ? 'destructive' : 'default')}
            className="flex items-center gap-2"
            loading={loading}
          >
            <Icon name={(variant === 'destructive' || confirmButtonVariant === 'destructive') ? 'trash' : 'Check'} size={16} />
            {confirmButtonText || confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// Hook for easier usage
export function useConfirmationDialog() {
  const [open, setOpen] = React.useState(false)
  const [config, setConfig] = React.useState<Omit<ConfirmationDialogProps, 'open' | 'onOpenChange'> | null>(null)

  const confirm = React.useCallback((
    title: string,
    description: string,
    onConfirm: () => void,
    options?: {
      confirmLabel?: string
      cancelLabel?: string
      variant?: 'default' | 'destructive'
      onCancel?: () => void
    }
  ) => {
    setConfig({
      title,
      description,
      onConfirm,
      confirmLabel: options?.confirmLabel,
      cancelLabel: options?.cancelLabel,
      variant: options?.variant,
      onCancel: options?.onCancel,
    })
    setOpen(true)
  }, [])

  const dialog = config ? (
    <ConfirmationDialog
      open={open}
      onOpenChange={setOpen}
      {...config}
    />
  ) : null

  return { confirm, dialog }
}

